import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter, TrendingUp, TrendingDown, Minus } from "lucide-react";
import LapCard from "./LapCard";
import { formatLapTime } from "@/utils/timeFormat";
import type { Lap, LapStatistics } from "@/types";

interface LapListProps {
  laps: Lap[];
  statistics?: LapStatistics;
  isLoading?: boolean;
  onEdit?: (lap: Lap) => void;
  onDelete?: (lapId: string) => void;
}

export default function LapList({
  laps,
  statistics,
  isLoading,
  onEdit,
  onDelete,
}: LapListProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [filter, setFilter] = useState<"all" | "valid" | "invalid">("all");
  const [sortBy, setSortBy] = useState<"lapNumber" | "lapTime" | "maxSpeed">(
    "lapNumber",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  if (isLoading) {
    return (
 <div className="space-y-4">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
 className="bg-white rounded-lg border border-secondary-200 p-4 animate-pulse"
          >
 <div className="h-6 bg-secondary-200 rounded mb-3 w-1/4"></div>
 <div className="h-8 bg-secondary-200 rounded mb-3 w-1/2"></div>
 <div className="space-y-2">
 <div className="h-4 bg-secondary-200 rounded w-3/4"></div>
 <div className="h-4 bg-secondary-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (laps.length === 0) {
    return (
 <div className="text-center py-12">
        <p className="text-lg text-secondary-500 mb-2">
          {t("pages:sessions.lapList.noLapsRecorded")}
        </p>
        <p className="text-sm text-secondary-400">
          {t("pages:sessions.lapList.uploadTelemetryHint")}
        </p>
      </div>
    );
  }

  // Filter laps
  const filteredLaps = laps.filter((lap) => {
    if (filter === "valid") return lap.isValid;
    if (filter === "invalid") return !lap.isValid;
    return true;
  });

  // Sort laps
  const sortedLaps = [...filteredLaps].sort((a, b) => {
    let aValue: number | string;
    let bValue: number | string;

    switch (sortBy) {
      case "lapNumber":
        aValue = a.lapNumber;
        bValue = b.lapNumber;
        break;
      case "lapTime":
        aValue = a.lapTime;
        bValue = b.lapTime;
        break;
      case "maxSpeed":
        aValue = a.maxSpeed || 0;
        bValue = b.maxSpeed || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const bestLapTime = statistics?.bestLapTime;

  const handleSortChange = (
    newSortBy: "lapNumber" | "lapTime" | "maxSpeed",
  ) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
  };

  const getTrendIcon = () => {
    if (!statistics) return null;

    switch (statistics.improvementTrend) {
      case "improving":
 return <TrendingDown className="h-4 w-4 text-green-600" />;
      case "declining":
 return <TrendingUp className="h-4 w-4 text-red-600" />;
      default:
 return <Minus className="h-4 w-4 text-secondary-400" />;
    }
  };

  return (
 <div className="space-y-6">
      {/* Statistics Summary */}
      {statistics && (
 <div className="bg-white rounded-lg border border-secondary-200 p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">
            {t("pages:sessions.lapList.sessionStatistics")}
          </h3>
 <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="text-sm text-secondary-500">
                {t("pages:sessions.lapList.totalLaps")}
              </div>
              <div className="text-2xl font-bold text-secondary-900">
                {statistics.totalLaps}
              </div>
            </div>
            <div>
              <div className="text-sm text-secondary-500">
                {t("pages:sessions.lapList.validLaps")}
              </div>
              <div className="text-2xl font-bold text-secondary-900">
                {statistics.validLaps}
              </div>
            </div>
            <div>
              <div className="text-sm text-secondary-500">
                {t("pages:sessions.lapList.bestLap")}
              </div>
              <div className="text-2xl font-bold text-secondary-900 font-mono">
                {formatLapTime(statistics.bestLapTime)}
              </div>
            </div>
            <div>
              <div className="text-sm text-secondary-500">
                {t("pages:sessions.lapList.averageLap")}
              </div>
              <div className="text-2xl font-bold text-secondary-900 font-mono">
                {formatLapTime(statistics.averageLapTime)}
              </div>
            </div>
            <div>
              <div className="text-sm text-secondary-500">
                {t("pages:sessions.lapList.stdDeviation")}
              </div>
 <div className="text-2xl font-bold text-secondary-900 font-mono">
                {formatLapTime(statistics.standardDeviation)}
              </div>
            </div>
          </div>

 <div className="mt-4 pt-4 border-t border-secondary-100">
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-2">
              <span className="text-sm text-secondary-500">
                {t("pages:sessions.lapList.trend")}
              </span>
              {getTrendIcon()}
              <span className="text-sm font-medium text-secondary-700">
                {t(
                  `pages:sessions.lapList.trend${statistics.improvementTrend.charAt(0).toUpperCase()}${statistics.improvementTrend.slice(1)}`,
                )}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-secondary-500">
                {t("pages:sessions.lapList.consistency")}
              </span>
 <span className="text-sm font-medium text-secondary-700">
                  {statistics.consistencyRating}/100
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Sorting */}
 <div className="bg-white rounded-lg border border-secondary-200 p-4">
 <div className="flex flex-wrap items-center justify-between gap-4">
 <div className="flex items-center space-x-4">
 <div className="flex items-center space-x-2">
 <Filter className="h-4 w-4 text-secondary-400" />
              <span className="text-sm font-medium text-secondary-700">
                {t("pages:sessions.lapList.filter")}:
              </span>
              <select
                value={filter}
                onChange={(e) =>
                  setFilter(e.target.value as "all" | "valid" | "invalid")
                }
                className="text-sm border border-secondary-300 rounded px-2 py-1"
              >
                <option value="all">
                  {t("pages:sessions.lapList.allLaps")}
                </option>
                <option value="valid">
                  {t("pages:sessions.lapList.validOnly")}
                </option>
                <option value="invalid">
                  {t("pages:sessions.lapList.invalidOnly")}
                </option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-secondary-700">
                {t("pages:sessions.lapList.sortBy")}:
              </span>
              {["lapNumber", "lapTime", "maxSpeed"].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleSortChange(
                      option as "lapNumber" | "lapTime" | "maxSpeed",
                    )
                  }
 className={`px-3 py-1 text-sm rounded-lg capitalize ${
                    sortBy === option
                      ? "bg-primary-600 text-white"
                      : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                  }`}
                >
                  {option === "lapNumber"
                    ? t("pages:sessions.lapList.lapNumberShort")
                    : option === "lapTime"
                      ? t("pages:sessions.lapList.time")
                      : t("pages:sessions.lapList.maxSpeed")}
                  {sortBy === option && (
 <span className="ml-1">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-secondary-600">
            {t("pages:sessions.lapList.lapsShownCount", {
              shown: sortedLaps.length,
              total: laps.length,
            })}
          </div>
        </div>
      </div>

      {/* Lap Grid */}
 <div className="grid grid-cols-1 gap-4">
        {sortedLaps.map((lap) => (
          <LapCard
            key={lap.id}
            lap={lap}
            bestLapTime={bestLapTime}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}
